import React, { useState } from "react";
import { CaretRight, CaretDown } from "phosphor-react";

const DynamicTable = ({
  columns,
  data,
  onRowSelect,
  selectedRows = [],
  selectAll,
  onSelectAll,
  expandable = false,
  renderExpandedContent,
  actionIcons = [],
  onActionClick = () => {},
  searchValue = "",
  enableSelection = false,
  isRowSelectable = () => true,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpand = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };
  function getValue(obj, path) {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  }
  return (
    <div className="container-fluid">
      <div className="overflow-scroll">
        <table className="table table-rows">
          <thead className="table-header" style={{ backgroundColor: "black" }}>
            <tr>
              {enableSelection && (
                <th>
                  <div className="form-check ps-3 mb-0">
                    <input
                      className={`form-check-input rounded-1 border-dark py-2 px-2 ${
                        selectAll ? "" : "opacity-25"
                      }`}
                      type="checkbox"
                      checked={selectAll}
                      onChange={onSelectAll}
                      disabled={data.every((row) => !isRowSelectable(row))}
                    />
                  </div>
                </th>
              )}
              {expandable && <th></th>}
              {columns.map((column) => (
                <th key={column.columnKey} style={{ cursor: "pointer" }}>
                  <div className="d-flex position-relative">
                    <div>{column.label}</div>
                  </div>
                </th>
              ))}
              {(() => {
                // Always show action column header if actionIcons is provided
                // Since different rows may have different icons, we should always show the header
                // when actionIcons is provided (either as function or array)
                if (typeof actionIcons === "function") {
                  // If it's a function, always show header (different rows may have different icons)
                  return <th></th>;
                } else {
                  // If it's an array, check if it has items
                  return actionIcons && actionIcons.length > 0 ? (
                    <th></th>
                  ) : null;
                }
              })()}
            </tr>
          </thead>
          <tbody className="table-body">
            {data.map((row, index) => (
              <React.Fragment key={row.id || index}>
                <tr>
                  {enableSelection && (
                    <td>
                      <div className="form-check ps-3 mb-0">
                        <input
                          className={`form-check-input rounded-0 border-dark py-2 px-2 ${
                            selectedRows.includes(row.id) ? "" : "opacity-25"
                          }`}
                          type="checkbox"
                          checked={selectedRows.includes(row.id)}
                          onChange={() => onRowSelect(row.id)}
                          disabled={!isRowSelectable(row)}
                        />
                      </div>
                    </td>
                  )}
                  {expandable && (
                    <td>
                      <div className="form-check ps-3 mb-0">
                        {expandedRows.has(row.id) ? (
                          <CaretDown
                            size={18}
                            className="cursor-pointer"
                            onClick={() => toggleRowExpand(row.id)}
                          />
                        ) : (
                          <CaretRight
                            size={18}
                            className="cursor-pointer"
                            onClick={() => toggleRowExpand(row.id)}
                          />
                        )}
                      </div>
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.columnKey}
                      className={column.className || ""}
                    >
                      {column.render
                        ? column.render(getValue(row, column.columnKey), row)
                        : getValue(row, column.columnKey)}
                    </td>
                  ))}
                  {(() => {
                    // Support both array and function for actionIcons
                    const icons =
                      typeof actionIcons === "function"
                        ? actionIcons(row)
                        : actionIcons;
                    return icons && icons.length > 0 ? (
                      <td>
                        {icons.map((Icon, idx) => (
                          <Icon
                            key={idx}
                            size={24}
                            className="cursor-pointer ms-2"
                            onClick={() => onActionClick(idx, row)}
                          />
                        ))}
                      </td>
                    ) : null;
                  })()}
                </tr>
                {expandable && expandedRows.has(row.id) && (
                  <tr>
                    <td
                      colSpan={
                        columns.length +
                        (enableSelection ? 2 : 1) +
                        (() => {
                          const icons =
                            typeof actionIcons === "function"
                              ? data.length > 0
                                ? actionIcons(data[0])
                                : []
                              : actionIcons;
                          return icons && icons.length > 0 ? 1 : 0;
                        })()
                      }
                    >
                      {renderExpandedContent(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicTable;
